import { useTranslation } from 'react-i18next';
import type { OwnerAppointment } from '@/api/appointments/appointmentsApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';

interface CancelVisitDialogProps {
  visit: OwnerAppointment;
  isSubmitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CancelVisitDialog({ visit, isSubmitting, onConfirm, onCancel }: CancelVisitDialogProps) {
  const { t } = useTranslation(['clients', 'common']);
  return (
    <ModalOverlay onRequestClose={onCancel} variant="sheet">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto bg-zoowhite border border-zooborder rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:pb-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-zooerrorbg">
          <Icon name="X" className="text-xl text-zooerrortext" />
        </div>
        <h2 className="text-lg font-bold text-zooindigo">
          {t('clients:visits.cancelDialog.title', {
            petName: visit.patientName,
            date: formatIsoDateToDayAndMonth(visit.date),
            time: visit.startTime,
          })}
        </h2>
        <p className="text-sm text-zoomuted mt-1.5">{t('clients:visits.cancelDialog.hint')}</p>
        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t('clients:visits.cancelDialog.keep')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            isLoading={isSubmitting}
            onClick={onConfirm}
          >
            {t('clients:visits.cancelDialog.confirm')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
